import { createContext } from "react";
import type { Language } from "../constants/enums";

/**
 * Параметры для подстановки в строки локализации (позиционные, как в Rust println!)
 */
export type TranslationParams = Array<string | number>;

/**
 * Функция перевода с позиционными параметрами (Rust-style)
 */
export interface TranslateFunction {
  /**
   * Простой перевод без параметров
   * @example t('common.save') // → "Save"
   */
  (key: string | undefined | null): string;

  /**
   * Перевод с явным указанием локали (без параметров)
   * @example t('language.en', Language.RU) // → "Английский"
   */
  (key: string | undefined | null, locale: Language): string;

  /**
   * Перевод с явным указанием локали и параметрами
   * @example t('notification.minutesAgo', Language.RU, 5) // → "5 минут назад"
   */
  (
    key: string | undefined | null,
    locale: Language,
    ...params: TranslationParams
  ): string;

  /**
   * Перевод с параметрами (текущая локаль) - строка как первый параметр
   * @example t('message.sent', 'John', 3) // → "John sent 3 items"
   */
  (
    key: string | undefined | null,
    firstParam: string,
    ...params: TranslationParams
  ): string;

  /**
   * Перевод с параметрами (текущая локаль) - число как первый параметр
   * @example t('notification.minutesAgo', 5) // → "5 minutes ago"
   */
  (
    key: string | undefined | null,
    firstParam: number,
    ...params: TranslationParams
  ): string;
}

/**
 * Options for date formatting
 */
export interface DateFormatOptions {
  /** Include year in output (default: true) */
  year?: boolean;
  /** Include month in output (default: true) */
  month?: boolean;
  /** Include day in output (default: true) */
  day?: boolean;
  /** Include time in output (default: false) */
  time?: boolean;
}

/**
 * Function to format dates according to user's locale
 */
export interface FormatDateFunction {
  /**
   * Format date with default options (year, month, day)
   * @example formatDate('2024-01-15') // → "15.01.2024" (ru) or "01/15/2024" (en)
   */
  (date: string | Date): string;

  /**
   * Format date with custom options
   * @example formatDate('2024-01-15', { time: true }) // → "15.01.2024, 12:00"
   */
  (date: string | Date, options: DateFormatOptions): string;
}

export type LocalizationContextType = {
  locale: Language;
  t: TranslateFunction;
  setLocale: (locale: Language) => void;
  formatDate: FormatDateFunction;
};

const LocalizationContext = createContext<LocalizationContextType | undefined>(
  undefined,
);

export { LocalizationContext };
